import React, { useState, useEffect } from 'react';
import Header from './Header.jsx';
import Navigator from './Navigator.jsx';
import "../css/Search.css";
import { motion, AnimatePresence } from 'framer-motion';
import { domain } from './Domain.jsx';
import EventCard from './EventCard.jsx';
import Footer from './Footer.jsx';
import { useLocation } from 'react-router-dom';
import useApiRequest from './useApiRequest.jsx';

const Search = () => {
  const { getRequest } = useApiRequest();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const location = useLocation();

  // Helper function to extract query parameter
  const getQuery = () => {
    return new URLSearchParams(location.search).get('q');
  };

  const truncateString = (str) => {
    return str.length > 90 ? str.substring(0, 89) + "..." : str;
  };

  // Function to format the date to 'August 3, 2023'
  function formatDate(date) {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  }

  useEffect(() => {
    const fetchData = async () => {
      const query = getQuery();
      if (query) {
        setLoading(true);
        try {
          const response = await getRequest(`${domain}/event-details?q=${encodeURIComponent(query)}`);
          setResults(response.body || []);
          setCurrentPage(1); // Reset to first page on new search
        } catch (error) {
          console.error('Error fetching search results', error);
          setResults([]);
        } finally {
          setLoading(false);
        }
      } else {
        setResults([]);
        setLoading(false);
      }
    };

    fetchData();
  }, [location.search]);

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentResults = results.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(results.length / itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className='animate__animated animate__fadeIn'>
      <Header />
      <div className='container search pt-4 pb-4'>
        {loading ? (
          <p className="loading-text">Loading...</p>
        ) : (
          <div>
            <h2 className='fs-4 pb-3'>Search Results For "{getQuery() || 'No Query'}"</h2>
            {results.length > 0 ? (
              <>
                <div className='card-container'>
                  {currentResults.map(item => (
                        <EventCard key={item.id} event={item} />
                    ))}
                </div>
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center align-items-center gap-2 mt-4">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="btn btn-outline-secondary"
                      style={{ padding: '5px 15px', fontSize: '14px' }}
                    >
                      Previous
                    </button>
                    {[...Array(totalPages).keys()].map(page => (
                      <button
                        key={page + 1}
                        onClick={() => handlePageChange(page + 1)}
                        className={`btn ${currentPage === page + 1 ? 'btn-secondary' : 'btn-outline-secondary'}`}
                        style={{ padding: '5px 15px', fontSize: '14px' }}
                      >
                        {page + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="btn btn-outline-secondary"
                      style={{ padding: '5px 15px', fontSize: '14px' }}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            ) : (
              <p className="no-results">No results found.</p>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Search;